import { useMutation, type ApolloCache } from "@apollo/client";

import {
  ADD_OWN_OEM_PART_TO_TICKET_MUTATION,
  ADD_TICKET_ASSIGNEE_MUTATION,
  ADD_TICKET_ATTACHMENT_MUTATION,
  ADD_TICKET_FOLLOWER_MUTATION,
  ADD_TICKET_RESOURCE_MUTATION,
  ADD_TICKET_TIME_TRACKER_LOG_MUTATION,
  ASSIGN_OWN_OEM_TICKET_ASSETS_MUTATION,
  ASSIGN_OWN_OEM_TICKET_COMPANY_MUTATION,
  EDIT_OWN_OEM_PART_ON_TICKET_MUTATION,
  LINK_TICKETS,
  REMOVE_OWN_OEM_PART_FROM_TICKET_MUTATION,
  REMOVE_TICKET_ASSIGNEE_MUTATION,
  REMOVE_TICKET_ATTACHMENT_MUTATION,
  REMOVE_TICKET_FOLLOWER_MUTATION,
  REMOVE_TICKET_RESOURCES_MUTATION,
  REMOVE_TIME_TRACKER_LOG_FROM_TICKET_MUTATION,
  UNLINK_TICKETS,
  UPDATE_DESCRIPTION_TICKET_MUTATION,
  UPDATE_OWN_OEM_TICKET_MUTATION,
  UPDATE_OWN_OEM_TICKET_TYPE_MUTATION,
  UPDATE_SOLUTION_TICKET_MUTATION,
  UPDATE_TICKET_TIME_TRACKER_LOG_MUTATION,
  UPDATE_TITLE_TICKET_MUTATION,
} from "~/api";

import type { Id } from "./types";

const REFETCH = ["getOwnOemTicketById"];

// Attaching/detaching an asset (or swapping the company) re-derives the work
// order's `teams` server-side, which changes who may be assigned. Refetching
// the work order alone refreshes the team members listed in the assignee
// dropdown but not `listAssignableUsers` — the gate that decides whether each
// one is selectable — so the new team's members render disabled until a page
// reload. Both writes must refetch the pair.
const REFETCH_WITH_ASSIGNABLE_USERS = [...REFETCH, "listAssignableUsers"];

const evictOwnOemActivityLogs = (cache: ApolloCache<object>) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "listOwnOemActivityLogs",
  });
  cache.gc();
};

// The same asset/company writes move the chat composer's @-mention list: the
// asset's company is what puts facility (external) users in it, so a work order
// that gains its first asset keeps an OEM-only list until a page reload.
// Evicted rather than named in `refetchQueries`, because a name-based refetch
// only reaches *active* queries — the Comments/External Chat tab is usually
// unmounted while the sidebar writes, and would then read the stale cached list
// on its next mount.
const evictTicketMentionableUsers = (cache: ApolloCache<object>) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "listOwnTicketMentionableUsers",
  });
  cache.gc();
};

/**
 * Generic update — schedule, due date, status, company contacts, assignee, etc.
 * accepted by the server's `updateOwnOemTicket` input.
 *
 * The server input is named `ticketId`, so the variables exposed here keep
 * the legacy key. Local code should still use `workOrder*` names.
 */
export const useUpdateWorkOrder = () => {
  const [run, { loading, error }] = useMutation(UPDATE_OWN_OEM_TICKET_MUTATION);

  return {
    loading,
    error,
    updateWorkOrder: (input: { ticketId: Id; [k: string]: unknown }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useUpdateWorkOrderTitle = () => {
  const [run, { loading, error }] = useMutation(UPDATE_TITLE_TICKET_MUTATION);

  return {
    loading,
    error,
    updateTitle: (input: { ticketId: Id; title: string }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useUpdateWorkOrderDescription = () => {
  const [run, { loading, error }] = useMutation(
    UPDATE_DESCRIPTION_TICKET_MUTATION,
  );

  return {
    loading,
    error,
    updateDescription: (input: { ticketId: Id; description: string }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useUpdateWorkOrderSolution = () => {
  const [run, { loading, error }] = useMutation(
    UPDATE_SOLUTION_TICKET_MUTATION,
  );

  return {
    loading,
    error,
    updateSolution: (input: { ticketId: Id; solution: string }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useAddWorkOrderAssignee = () => {
  const [run, { loading, error }] = useMutation(ADD_TICKET_ASSIGNEE_MUTATION);
  return {
    loading,
    error,
    addAssignee: (input: { ticketId: Id; assignee: Id }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useRemoveWorkOrderAssignee = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_TICKET_ASSIGNEE_MUTATION,
  );
  return {
    loading,
    error,
    removeAssignee: (input: { ticketId: Id; assignee: Id }) =>
      run({
        variables: { input },
        update: (cache) => {
          evictOwnOemActivityLogs(cache);
        },
      }),
  };
};

export const useAddWorkOrderFollower = () => {
  const [run, { loading, error }] = useMutation(ADD_TICKET_FOLLOWER_MUTATION);
  return {
    loading,
    error,
    addFollower: (input: { ticketId: Id; follower: Id }) =>
      run({ variables: { input } }),
  };
};

export const useRemoveWorkOrderFollower = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_TICKET_FOLLOWER_MUTATION,
  );
  return {
    loading,
    error,
    removeFollower: (input: { ticketId: Id; follower: Id }) =>
      run({ variables: { input } }),
  };
};

export const useAddWorkOrderResource = () => {
  const [run, { loading, error }] = useMutation(ADD_TICKET_RESOURCE_MUTATION);
  return {
    loading,
    error,
    addResource: (input: { ticketId: Id; resource: Id }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveWorkOrderResources = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_TICKET_RESOURCES_MUTATION,
  );
  return {
    loading,
    error,
    removeResources: (input: { ticketId: Id; resources: Id[] }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useAssignWorkOrderPart = () => {
  const [run, { loading, error }] = useMutation(
    ADD_OWN_OEM_PART_TO_TICKET_MUTATION,
  );
  return {
    loading,
    error,
    assignPart: (input: {
      ticketId: Id;
      inventoryParts: Array<{ part: Id; quantity: number; assetId?: Id }>;
    }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveWorkOrderPart = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_OWN_OEM_PART_FROM_TICKET_MUTATION,
  );
  return {
    loading,
    error,
    removePart: (input: { ticketId: Id; partId: Id; assetId: Id }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateWorkOrderPartQuantity = () => {
  const [run, { loading, error }] = useMutation(
    EDIT_OWN_OEM_PART_ON_TICKET_MUTATION,
  );
  return {
    loading,
    error,
    updatePartQuantity: (input: {
      ticketId: Id;
      partId: Id;
      quantity: number;
    }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useAddWorkOrderAttachments = () => {
  const [run, { loading, error }] = useMutation(ADD_TICKET_ATTACHMENT_MUTATION);
  return {
    loading,
    error,
    addAttachments: (input: {
      ticketId: Id;
      attachments: Array<{
        url: string;
        name: string;
        type: string;
        size?: number;
      }>;
    }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveWorkOrderAttachment = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_TICKET_ATTACHMENT_MUTATION,
  );
  return {
    loading,
    error,
    removeAttachment: (input: { ticketId: Id; attachmentId: Id }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useLinkWorkOrders = () => {
  const [run, { loading, error }] = useMutation(LINK_TICKETS);
  return {
    loading,
    error,
    link: (input: { ticketId: Id; linkedTicketId: Id }) =>
      run({
        variables: input,
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useUnlinkWorkOrders = () => {
  const [run, { loading, error }] = useMutation(UNLINK_TICKETS);
  return {
    loading,
    error,
    unlink: (input: { ticketId: Id; linkedTicketId: Id }) =>
      run({
        variables: input,
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

/**
 * Change a work order's type (and the title / type-driven fields that come
 * with it: description, parts, custom fields).
 *
 * Server input is `InputUpdateOwnOemTicketType`; the GraphQL field is still
 * named with the legacy `ticket*` keys. The mutation returns the full updated
 * ticket (`TicketSingleData`) so Apollo's normalized cache updates in place —
 * we also refetch `getOwnOemTicketById` to be safe.
 */
export interface UpdateWorkOrderTypeInput {
  ticketId: Id;
  /** Target work order type id. */
  ticketType: Id;
  title: string;
  /** Serialized rich-text description; empty string when the type hides it. */
  description?: string;
  inventoryParts?: Array<{
    part: Id;
    quantity: number;
    addedBy?: Id;
    assetId?: Id;
  }>;
  customFields?: Array<{
    _id?: Id;
    fieldId: Id;
    value?: unknown;
    values?: unknown[];
  }>;
}

export const useUpdateWorkOrderType = () => {
  const [run, { loading, error }] = useMutation(
    UPDATE_OWN_OEM_TICKET_TYPE_MUTATION,
  );

  return {
    loading,
    error,
    updateWorkOrderType: (input: UpdateWorkOrderTypeInput) =>
      run({
        variables: { input },
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

interface TimeTrackerLogInput {
  description?: string;
  startDateTime: string;
  endDateTime: string;
  isBillable: boolean;
  ticketTag: Id;
  timeElapsedInSeconds: number;
  timezone?: string;
  customFields?: Array<{
    fieldId: Id;
    value?: unknown;
    values?: unknown[];
  }>;
}

const TIME_TRACKING_REFETCH = [
  "getOwnOemTicketById",
  "getWorkOrderForTimeTracking",
];

export const useAddWorkOrderTimeLog = () => {
  const [run, { loading, error }] = useMutation(
    ADD_TICKET_TIME_TRACKER_LOG_MUTATION,
  );
  return {
    loading,
    error,
    addTimeLog: (input: {
      ticketId: Id;
      timeTrackerLog: TimeTrackerLogInput;
    }) =>
      run({
        variables: { input },
        refetchQueries: TIME_TRACKING_REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateWorkOrderTimeLog = () => {
  const [run, { loading, error }] = useMutation(
    UPDATE_TICKET_TIME_TRACKER_LOG_MUTATION,
  );
  return {
    loading,
    error,
    updateTimeLog: (input: {
      ticketId: Id;
      timeLogId: Id;
      timeTrackerLog: TimeTrackerLogInput;
    }) =>
      run({
        variables: { input },
        refetchQueries: TIME_TRACKING_REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveWorkOrderTimeLog = () => {
  const [run, { loading, error }] = useMutation(
    REMOVE_TIME_TRACKER_LOG_FROM_TICKET_MUTATION,
  );
  return {
    loading,
    error,
    removeTimeLog: (input: { ticketId: Id; timeLogId: Id }) =>
      run({
        variables: { input },
        refetchQueries: TIME_TRACKING_REFETCH,
        awaitRefetchQueries: true,
      }),
  };
};

export const useAssignWorkOrderCompany = () => {
  const [run, { loading, error }] = useMutation(
    ASSIGN_OWN_OEM_TICKET_COMPANY_MUTATION,
  );
  return {
    loading,
    error,
    assignCompany: (input: { ticketId: Id; companyId: Id }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH_WITH_ASSIGNABLE_USERS,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictTicketMentionableUsers(cache);
        },
      }),
  };
};

export const useAssignWorkOrderAssets = () => {
  const [run, { loading, error }] = useMutation(
    ASSIGN_OWN_OEM_TICKET_ASSETS_MUTATION,
  );
  return {
    loading,
    error,
    assignAssets: (input: { ticketId: Id; assetIds: Id[] }) =>
      run({
        variables: { input },
        refetchQueries: REFETCH_WITH_ASSIGNABLE_USERS,
        awaitRefetchQueries: true,
        update: (cache) => {
          evictTicketMentionableUsers(cache);
        },
      }),
  };
};
